
// Helper: parse hex to RGB
export function hexToRgb(hex: string): { r: number; g: number; b: number } {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  // Return a default safe color if parsing fails
  return result
    ? { r: parseInt(result[1], 16), g: parseInt(result[2], 16), b: parseInt(result[3], 16) }
    : { r: 0, g: 255, b: 65 };
}

// Helper: shift hue for layer variety
export function shiftHue(hex: string, degrees: number): string {
  // Ensure the input is a valid hex string
  if (typeof hex !== 'string' || !/^#?[0-9A-Fa-f]{6}$/.test(hex)) {
    throw new Error(`Invalid hex color: "${hex}"`);
  }

  const { r, g, b } = hexToRgb(hex);
  const rr = r / 255,
    gg = g / 255,
    bb = b / 255;
  const max = Math.max(rr, gg, bb),
    min = Math.min(rr, gg, bb);
  let h = 0;
  const s = max === 0 ? 0 : (max - min) / max;
  const v = max;

  if (max !== min) {
    const d = max - min;
    if (max === rr) h = ((gg - bb) / d + (gg < bb ? 6 : 0)) / 6;
    else if (max === gg) h = ((bb - rr) / d + 2) / 6;
    else h = ((rr - gg) / d + 4) / 6;
  }

  // Apply hue shift
  h = ((h * 360 + degrees) % 360) / 360;
  if (h < 0) h += 1;

  const i = Math.floor(h * 6);
  const f = h * 6 - i;
  const p = v * (1 - s);
  const q = v * (1 - f * s);
  const t = v * (1 - (1 - f) * s);

  let ro = 0,
    go = 0,
    bo = 0;
  switch (i % 6) {
    case 0:
      ro = v;
      go = t;
      bo = p;
      break;
    case 1:
      ro = q;
      go = v;
      bo = p;
      break;
    case 2:
      ro = p;
      go = v;
      bo = t;
      break;
    case 3:
      ro = p;
      go = q;
      bo = v;
      break;
    case 4:
      ro = t;
      go = p;
      bo = v;
      break;
    case 5:
      ro = v;
      go = p;
      bo = q;
      break;
  }

  const toHex = (n: number) => Math.round(n * 255).toString(16).padStart(2, '0');
  return `#${toHex(ro)}${toHex(go)}${toHex(bo)}`;
}

// Generate 3 layer accent colors from theme
export function getLayerColors(themeColor: string) {
  // Validate themeColor before processing
  if (typeof themeColor !== 'string' || !/^#?[0-9A-Fa-f]{6}$/.test(themeColor)) {
    throw new Error(`Invalid theme color: "${themeColor}"`);
  }

  return {
    layer02: shiftHue(themeColor, 30),
    layer03: shiftHue(themeColor, -40),
    layer04: shiftHue(themeColor, 60),
  };
}
